// Prototype. Do not instantiate, but extend this class in order to create custom filters.
// https://www.ag-grid.com/angular-data-grid/component-floating-filter/

import { Component, OnDestroy } from '@angular/core';
import { IFloatingFilterAngularComp } from 'ag-grid-angular';
import { FilterChangedEvent, IFloatingFilterParams, TextFilterParams } from 'ag-grid-community';
import { UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ISimpleFilterModelType } from './ag-text-floating-filter/ag-text-floating-filter.component';


export interface CustomFloatingFilterParams extends IFloatingFilterParams {
  // is floating filter read only
  readOnly: boolean;
  label?: string; // label to show if filter has no value
}

export interface CustomFloatingFilterViewModel {
  // input has blue bg if true
  hasValue: boolean,
  // click on input opens up filter menu if true
  // input is editable if false
  readOnly: boolean,
  label: string // label to show if filter has no value
  filterModel: any // parent filter model
}

@Component({
  template: '',
})
export abstract class AgFloatingFilterComponent implements IFloatingFilterAngularComp, OnDestroy {

  protected params: CustomFloatingFilterParams;
  // filter type, e.g. 'contains', 'equals' ... or null in case of our custom select filter
  protected lastType: string | null | undefined;
  protected _viewModel$: BehaviorSubject<CustomFloatingFilterViewModel> =
      new BehaviorSubject({ hasValue: false, readOnly: false, label: '', filterModel: null });
  protected componentDestroyed = new Subject<void>();
  public inputFormControl: UntypedFormControl = new UntypedFormControl(null);
  // view model for the template
  public viewModel$: Observable<CustomFloatingFilterViewModel> =
      this._viewModel$.asObservable();

  protected constructor() {
  }

  /**
   * Mandatory - The agInit(params) method is called on the floating filter once.
   * @param params
   */
  agInit(params: CustomFloatingFilterParams) {
    // init params
    this.params = params;
    // init View Model
    this._viewModel$.next({
      ...this._viewModel$.value,
      readOnly: this.getInitialReadOnly(),
      label: params.label,
    });
    // init filter type
    this.lastType = this.getDefaultType();
    // init debounce time
    const debounceMs = this.getDefaultDebounceMS();
    // sync parent filter (if not read-only)
    this.inputFormControl.valueChanges
        .pipe(debounceTime(debounceMs), takeUntil(this.componentDestroyed))
        .subscribe(() => {
          this.syncUpWithParentFilter();
        });
  }

  ngOnDestroy() {
    this.componentDestroyed.next();
  }

  /**
   * This method is relevant for text and number floating filters, as our custom select filters are
   * read-only
   * @protected
   */
  protected syncUpWithParentFilter(): void {
    // bail out if read-only
    if (this._viewModel$.value.readOnly) {
      return;
    }

    let value = this.inputFormControl.value;

    if ((this.params.filterParams as TextFilterParams).trimInput) {
      this.setFormControlValueProgrammatically(value.trim());
    }

    this.params.parentFilterInstance(filterInstance => {
      if (filterInstance) {
        // number filter is typed as number, but actually receives string values
        filterInstance.onFloatingFilterChanged(this.lastType || this.getDefaultType(), value);
      }
    });
  }

  /**
   * Gets called every time the parent filter changes. Your floating
   * filter would typically refresh its UI to reflect the new filter
   * state. The provided model is what the parent filter returns
   * from its getModel() method. The event is the FilterChangedEvent
   * that the grid fires.
   * @param filterModel parent filter model
   * @param event
   */
  onParentModelChanged(filterModel: any, event: FilterChangedEvent) {
    // cache last filter type
    this.lastType = filterModel?.filterType ? filterModel.filterType : this.getDefaultType();
    // When the filter is reset, we will receive 'null'
    if (!this.hasValue(filterModel)) {
      this.setFormControlValueProgrammatically(null);
      this.setViewModel({
        hasValue: this.hasValue(this.inputFormControl.value),
        readOnly: this.getInitialReadOnly(), // reset readOnly value
        label: this._viewModel$.value.label,
        filterModel
      });
    } else {
      this.setFormControlValueFromParentFilterModel(filterModel);
      this.setViewModel({
        hasValue: this.hasValue(this.inputFormControl.value),
        readOnly: !this.getInitialReadOnly() ?
            this.computeReadOnly(filterModel) :
            this._viewModel$.value.readOnly,
        label: this._viewModel$.value.label,
        filterModel
      });
    }
  }


  // ************************************ HELPERS *********************************************** //

  /**
   * check for some falsy values (we want 0 and boolean false to pass to support boolean and number
   * filters)
   * @param value
   * @private
   */
  private hasValue(value: any) {
    return value !== undefined && value !== null && value !== '';
  }


  /**
   * @param filterModel parent filter model
   * Override if we need to post-process filterModel value before storing it in form control
   */
  protected setFormControlValueFromParentFilterModel(filterModel: any): any {
    this.setFormControlValueProgrammatically(filterModel);
  }

  /**
   * Does not emit event
   * @param value
   * @protected
   */
  protected setFormControlValueProgrammatically(value: any) {
    this.inputFormControl.setValue(value, { emitEvent: false });
  }

  protected setViewModel(viewModel: CustomFloatingFilterViewModel) {
    this._viewModel$.next({ ...this._viewModel$, ...viewModel });
  }

  /**
   * Needs to be computed after parent filter changes as some configurations require the floating
   * filter to operate in read-only mode
   * @param filterModel
   * @protected
   */
  protected computeReadOnly(filterModel: any): boolean {
    // filterModel of provided 'text' and 'number' filters will include 'filterType'
    // if we ever create a custom filter that can be edited from a paired floating filter,
    // its filterModel needs to include 'filterType' as well!
    const type = filterModel?.type;
    const uneditableTypes: ISimpleFilterModelType[] = [
      'inRange', 'empty', 'blank', 'notBlank',
    ];
    // never allow editing if the filter is combined (i.e. has two parts)
    const isCombined = !!filterModel?.operator;
    return isCombined ||
        !type || // don't allow editing if we don't have a filter type, i.e. select filter
        // don't allow editing if filter type is among uneditable types
        (type && uneditableTypes.includes(type));
  }

  protected getInitialReadOnly() {
    return !!this.params.readOnly;
  }

  protected getDefaultDebounceMS(): number {
    return 500;
  }

  /**
   * Returns the default type ('contains', 'equals', etc.)
   * @protected
   */
  protected getDefaultType(): ISimpleFilterModelType {
    throw new Error('Method not implemented (override me).');
  }


  // ***************************** TEMPLATE BINDINGS ******************************************** //


  /**
   * Show parent filter pop-up
   */
  public showParentFilter(): void {
    if (this._viewModel$.value.readOnly) {
      this.params.showParentFilter();
    }
  }

  /**
   * Reset parent filter
   * Make sure that the method to clear parent filter exists on the parent filter!
   * onFloatingFilterChanged() should be present on the default filters
   */
  public resetParentFilter() {
    this.params.parentFilterInstance((instance: any) => {
      instance.onFloatingFilterChanged(this.getDefaultType(), null);
    });
  }

}
